import React, { useState, useEffect } from "react";
import { 
  Grid, 
  Box, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Paper,
  Container,
  Skeleton,
  Alert,
  Card,
  CardContent
} from "@mui/material";
import { 
  TrendingUp, 
  Speed, 
  CheckCircle, 
  Error,
  Refresh,
} from "@mui/icons-material";

// Import existing components
import RequestsOverTime from "../components/dashboard/RequestsOverTime";
import RequestStatusPieChart from "../components/dashboard/RequestStatusPieChart";
import MetricsCard from "../components/MetricsCard";
import ResponseTimeChart from "../components/dashboard/ResponseTimeChart";
import SystemHealthCard from "../components/dashboard/SystemHealthCard";
import ErrorRateChart from "../components/dashboard/ErrorRateChart";

// Import API utilities
import axiosInstance from "utils/axiosInstance";
import apiConfig from "config/appConfig";

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboardMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the new dashboard API endpoint
      const response = await axiosInstance.get(apiConfig.endpoints.dashboard.metrics);
      setMetrics(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching dashboard metrics:", err);
      setError("Failed to load dashboard metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardMetrics();
    const interval = setInterval(fetchDashboardMetrics, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchDashboardMetrics();
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {lastUpdated
            ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
            : "Loading..."}
        </Typography>
        <Refresh
          sx={{
            cursor: "pointer",
            color: "text.secondary",
            "&:hover": { color: "primary.main" },
            fontSize: isMobile ? 24 : 28,
          }}
          onClick={handleRefresh}
        />
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Requests"
            value={loading ? <Skeleton width={60} /> : metrics?.totalRequests.toLocaleString()}
            subtitle="All time"
            icon={<TrendingUp />}
            color="primary"
            loading={loading}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Success Rate"
            value={loading ? <Skeleton width={60} /> : `${metrics?.successRate}%`}
            subtitle="Last 24 hours"
            icon={<CheckCircle />}
            color="success"
            loading={loading}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Avg Response Time"
            value={loading ? <Skeleton width={60} /> : `${metrics?.avgResponseTime}ms`}
            subtitle="Last 24 hours"
            icon={<Speed />}
            color="info"
            loading={loading}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Requests/Hour"
            value={loading ? <Skeleton width={60} /> : metrics?.requestsPerHour}
            subtitle="Last 24 hours"
            icon={<TrendingUp />}
            trend={metrics?.trend}
            trendValue={`${metrics?.trendValue}/hr`}
            color="warning"
            loading={loading}
            size="small"
          />
        </Grid>
      </Grid>

      {/* Middle Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              height: "100%",
              width: "100%",
              borderRadius: '6px',
              p: isMobile ? 2 : 3,
              boxShadow: theme.shadows[2],
              "&:hover": { boxShadow: theme.shadows[4] },
            }}
          >
            <RequestStatusPieChart />
          </Paper>
        </Grid>

        {/* Response Time Chart */}
        <Grid item xs={12} lg={6}>
          <ResponseTimeChart />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        {/* Requests Over Time */}
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              height: 450,
              p: isMobile ? 2 : 3,
              boxShadow: theme.shadows[2],
              "&:hover": { boxShadow: theme.shadows[4] },
              borderRadius: '6px',
            }}
          >
            <RequestsOverTime />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ErrorRateChart />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
